import { pubsubLogger as log } from './logger';
import { objectValues, getKey, isFunction } from './common';
import { MESSAGE, EVENT, METHOD, API, CONTENT_TYPE } from '../constant';
import { generateUUID } from '../../../checkout/src/utils/common';

/* Error object to return in case of status 0 or try catch error */
const errorObj = {
  head: {},
  body: {
    objectType: 'genericError',
    resultInfo: {
      resultStatus: 'F',
      resultCode: '0000',
      resultMsg: MESSAGE.GENERIC_ERROR
    }
  }
};

/**
 * parse query parameter object and return & separated key value pair
 * @param {Object} query - key value pair of query parameter
 * @return {String} - & separated key value pair
 * adding export so that this can be used to generate query string for content type other than
 * application/json where we arent sending the data in head body but need to send query params
 * */
export const queryStr = query => {
  let output = '';
  Object.keys(query).forEach(key => {
    output += `${key}=${query[key]}&`;
  });
  return output;
};

export class Interceptor {
  interceptors = {};

  add = interceptor => {
    if (!isFunction(interceptor)) {
      // eslint-disable-next-line no-console
      console.error('interceptor should be of function type!');
      return null;
    }

    const key = generateUUID();
    this.interceptors[key] = interceptor;
    return this._remove.bind(this, key);
  };

  getAsArray = () => {
    return objectValues(this.interceptors);
  };

  /**
   * Private method to remove middleware
   * @param {string} key : Unique/UUID key for interceptor
   */
  _remove = key => {
    this.interceptors[key] && delete this.interceptors[key];
  };
}

/**
 * Loop over all the middleware attached to this instance and modify the request object as per their preferences
 *
 * @param {Object} payload - default request payload
 * @return {Object} - modified request payload
 * */
export function runMiddleware(payload) {
  let options = payload;
  options.query = {};
  const getInterceptors = getKey(this, 'middleware.getAsArray');

  if (isFunction(getInterceptors)) {
    getInterceptors().forEach(interceptor => {
      options = interceptor(options);
    });
  }
  if (options.url.search('/v2/') > -1) {
    options.data.head.version = 'v2';
  }

  return options;
}

/**
 * Convert a form data to url encoded form
 * @param {Object} obj - object
 */
export function urlEncodeData(obj) {
  let s = '';
  function encode(s) {
    return encodeURIComponent(s).replace(/%20/g, '+');
  }
  Object.keys(obj).forEach(key => {
    s += `${(s ? '&' : '') + encode(key)}=${encode(obj[key])}`;
  });
  return s;
}

/**
 * Send ajax request to native api server.
 *
 * @param {object} payload - option object contains required data - url,method,data
 * @returns {Promise<any>} - ajax promise object
 * @private
 */
function _ajax(payload) {
  let options = {};
  const callerContext = this;

  if (payload.method === 'GET') {
    options = payload;
    options.query = {};
  } else {
    // skip middleware in case of merchant server url
    options = payload.skipMiddleware
      ? payload
      : isFunction(getKey(this, 'runMiddleware')) && this.runMiddleware(payload);
  }

  return new Promise((resolve, reject) => {
    const xhr = new window.XMLHttpRequest();
    const url =
      options.url +
      (!getKey(payload, 'noQueryParam', false) && options.query
        ? `?${queryStr(options.query)}`
        : '');
    xhr.open(options.method, url);
    /* if content type is being passed through options then set that content type */
    if (options['Content-type']) {
      xhr.setRequestHeader('Content-Type', options['Content-type']);
    } else {
      xhr.setRequestHeader('Content-type', CONTENT_TYPE.JSON);
    }

    let payModeDetails = {};
    if (url.indexOf(API.PT) !== -1) {
      payModeDetails = {
        paymentMode: getKey(options, 'data.body.paymentMode'),
        channelCode: getKey(options, 'data.body.channelCode'),
        bankCode: getKey(options, 'data.body.issuingBankCode')
      };
    }

    log.info({ eventType: EVENT.API_INIT, url, ...payModeDetails });
    /* istanbul ignore next */
    xhr.onreadystatechange = function onReadyStateChange() {
      if (this.readyState === 4) {
        let data = {};

        try {
          data = JSON.parse(this.responseText);
          log.info({
            eventType: EVENT.API_STATUS,
            url,
            data,
            statusCode: this.status,
            ...payModeDetails
          });
        } catch (e) {
          data = {};
          if (this.status >= 400 && this.status <= 599) {
            log.info({
              eventType: EVENT.API_STATUS,
              url,
              data: errorObj,
              statusCode: this.status,
              ...payModeDetails
            });
            return reject(errorObj); // reject with generic error object
          }
        }

        // if no net status is 0
        if (this.status === 0) {
          log.info({
            eventType: EVENT.API_STATUS,
            url,
            data: errorObj,
            statusCode: this.status,
            ...payModeDetails
          });
          return reject(errorObj);
        }

        if (this.status >= 200 && this.status < 400) {
          const getInterceptors = getKey(callerContext, 'postFetch.getAsArray');
          if (isFunction(getInterceptors)) {
            getInterceptors().forEach(interceptor => {
              interceptor({ response: data, request: options });
            });
          }

          if (data && data.body) {
            return resolve(data);
          }
          return resolve(data);
        }

        if (this.status >= 400 && this.status <= 599) {
          if (data && data.body) {
            return reject(data);
          }
          return reject(data);
        }
      }
      return null;
    };
    if (options.data && options['Content-type'] === CONTENT_TYPE.FORM_URLENCODED) {
      xhr.send(urlEncodeData(options.data));
    } else if (options.data) {
      // POST,PUT request with data payload
      xhr.send(JSON.stringify(options.data));
    } else {
      // GET , DELETE request
      xhr.send();
    }
  });
}

/**
 * ajax POST request
 *
 * @param {string} url - ajax request url
 * @param {object} data - json data send over post request
 * @param {Boolean/object} skipMiddleware - boolean flag to skip running the middleware over request object
 * @returns {Promise<any>} - promise object contains response from server
 * @throws {Error} - throw if url is empty or not of string type
 */
export function post(url, data, skipMiddleware) {
  const options = {};
  options.method = METHOD.POST;
  log.debug({ eventType: METHOD.POST, url, data, skipMiddleware });
  if (!url) {
    throw new Error('url cannot be empty or null');
  }
  options.url = url;
  if (data) {
    options.data = data;
  }
  options.skipMiddleware = skipMiddleware || false;
  if (skipMiddleware && typeof skipMiddleware === 'object') {
    /* if the skipMiddleware is being passed as object it wil contain skipMiddleware key and
    content tye */
    options.skipMiddleware = skipMiddleware.skipMiddleware;
    options['Content-type'] = skipMiddleware['Content-type'];
  }
  return _ajax.call(this, options);
}

export function get(url, option) {
  const options = {};
  options.method = METHOD.GET;
  log.debug({ eventType: METHOD.GET, url, option });
  if (!url) {
    throw new Error('url cannot be empty or null');
  }
  options.url = url;
  options.skipMiddleware = (option && option.skipMiddleware) || false;
  options.noQueryParam = getKey(option, 'noQueryParam', false);
  return _ajax.call(this, options);
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Preprod-BnR/Deploy_checkoutjs/common/src/utils/http.js